import { useState } from "react";
import { getWorkspaceTimeZone, getTimeZoneAbbreviation } from "@/lib/utils";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, Clock, X, Pencil, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type ScheduledEmailRow = {
  id: string;
  workspaceId: string;
  userId: string;
  scheduledFor: string;
  status: "pending" | "sending" | "sent" | "failed" | "cancelled";
  sourceType: "one_off" | "mass_email";
  recipientEmail: string | null;
  recipientName: string | null;
  subject: string | null;
  attemptCount: number;
  lastError: string | null;
  sentAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
};

function formatCT(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: getWorkspaceTimeZone(),
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }) + " " + getTimeZoneAbbreviation(iso);
  } catch {
    return iso;
  }
}

function statusVariant(status: ScheduledEmailRow["status"]):
  | "default"
  | "secondary"
  | "destructive"
  | "outline" {
  switch (status) {
    case "pending":
      return "secondary";
    case "sending":
      return "default";
    case "sent":
      return "outline";
    case "failed":
      return "destructive";
    case "cancelled":
      return "outline";
  }
}

export function ScheduledEmailsList() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<"pending" | "sent" | "failed" | "cancelled" | "all">("pending");
  const [cancelTarget, setCancelTarget] = useState<ScheduledEmailRow | null>(null);
  const [editTarget, setEditTarget] = useState<ScheduledEmailRow | null>(null);

  const { data: rows = [], isLoading } = useQuery<ScheduledEmailRow[]>({
    queryKey: ["/api/scheduled-emails", statusFilter],
    queryFn: async () => {
      const url =
        statusFilter === "all"
          ? "/api/scheduled-emails"
          : `/api/scheduled-emails?status=${statusFilter}`;
      const res = await apiRequest("GET", url);
      return (await res.json()) as ScheduledEmailRow[];
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/scheduled-emails/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-emails"] });
      toast({ title: "Cancelled", description: "Scheduled email cancelled." });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to cancel",
        description: error?.message || "Could not cancel scheduled email.",
        variant: "destructive",
      });
    },
  });

  const updateTimeMutation = useMutation({
    mutationFn: async (vars: { id: string; scheduledFor: string }) => {
      return apiRequest("PATCH", `/api/scheduled-emails/${vars.id}`, {
        scheduledFor: vars.scheduledFor,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-emails"] });
      toast({ title: "Updated", description: "Scheduled time updated." });
      setEditTarget(null);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update",
        description: error?.message || "Could not update scheduled time.",
        variant: "destructive",
      });
    },
  });

  const filters: Array<{ value: typeof statusFilter; label: string }> = [
    { value: "pending", label: "Pending" },
    { value: "sent", label: "Sent" },
    { value: "failed", label: "Failed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "all", label: "All" },
  ];

  return (
    <div className="space-y-4" data-testid="scheduled-emails-list">
      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={statusFilter === f.value ? "default" : "outline"}
            onClick={() => setStatusFilter(f.value)}
            data-testid={`button-scheduled-filter-${f.value}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground" data-testid="scheduled-empty-state">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No {statusFilter === "all" ? "" : statusFilter} scheduled emails.</p>
          <p className="text-xs mt-1">
            Use the &ldquo;Schedule send&rdquo; button on any composer to queue a future email.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => {
            const isPending = row.status === "pending";
            return (
              <Card
                key={row.id}
                className="p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`scheduled-row-${row.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant={statusVariant(row.status)} data-testid={`badge-status-${row.id}`}>
                      {row.status}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {row.sourceType === "mass_email" ? "Mass email" : "One-off"}
                    </Badge>
                    {row.attemptCount > 0 && (
                      <Badge variant="outline" className="text-xs">
                        attempt {row.attemptCount}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm font-medium truncate" data-testid={`text-subject-${row.id}`}>
                    {row.subject || "(no subject)"}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    To: {row.recipientName ? `${row.recipientName} <${row.recipientEmail || "—"}>` : (row.recipientEmail || "—")}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {row.status === "sent" && row.sentAt
                      ? `Sent ${formatCT(row.sentAt)}`
                      : row.status === "cancelled" && row.cancelledAt
                      ? `Cancelled ${formatCT(row.cancelledAt)}`
                      : `Scheduled for ${formatCT(row.scheduledFor)}`}
                  </div>
                  {row.lastError && row.status === "failed" && (
                    <div className="text-xs text-destructive flex items-start gap-1 mt-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span className="truncate">{row.lastError}</span>
                    </div>
                  )}
                  {row.status === "sent" && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <CheckCircle2 className="w-3 h-3 text-green-600" />
                      Delivered
                    </div>
                  )}
                </div>
                {isPending && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditTarget(row)}
                      data-testid={`button-edit-time-${row.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                      Edit time
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCancelTarget(row)}
                      data-testid={`button-cancel-${row.id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1.5" />
                      Cancel
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel scheduled email?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget?.subject
                ? `"${cancelTarget.subject}" to ${cancelTarget.recipientEmail || "—"} will not be sent.`
                : "This scheduled email will not be sent."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-confirm-cancel-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (cancelTarget) {
                  cancelMutation.mutate(cancelTarget.id);
                  setCancelTarget(null);
                }
              }}
              data-testid="button-confirm-cancel-confirm"
            >
              Cancel send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit time reuses the shared scheduling dialog; on confirm it
          PATCHes the row instead of POSTing a new one. */}
      <ScheduledEmailDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        sourceType={editTarget?.sourceType ?? "one_off"}
        recipientSummary={
          editTarget
            ? `Reschedule ${editTarget.recipientEmail || "email"}`
            : undefined
        }
        subjectSummary={editTarget?.subject || undefined}
        initialScheduledFor={editTarget?.scheduledFor}
        confirmLabel="Update time"
        buildPayload={() => null}
        onCustomSubmit={async (scheduledForIso) => {
          if (!editTarget) return;
          await updateTimeMutation.mutateAsync({
            id: editTarget.id,
            scheduledFor: scheduledForIso,
          });
        }}
      />
    </div>
  );
}
